import React from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SCHEDULE_ICONS = [
  { value: 'Church', label: 'Church' },
  { value: 'Music', label: 'Music' },
  { value: 'Camera', label: 'Camera' },
  { value: 'UtensilsCrossed', label: 'Food/Dining' },
  { value: 'Heart', label: 'Heart' },
  { value: 'Clock', label: 'Clock' },
  { value: 'MapPin', label: 'Location' },
  { value: 'Car', label: 'Transport' },
  { value: 'PartyPopper', label: 'Party' },
  { value: 'Gift', label: 'Gift' },
  { value: 'Flower2', label: 'Flower' },
  { value: 'Sparkles', label: 'Sparkles' },
];

const CmsScheduleSection = ({ content, onChange }) => {
  const schedule = content?.schedule || {};
  const items = schedule.items || [];

  const updateSchedule = (field, value) => {
    onChange({
      ...content,
      schedule: { ...schedule, [field]: value },
    });
  };

  const updateItem = (index, field, value) => {
    const updated = [...items];
    updated[index] = { ...updated[index], [field]: value };
    updateSchedule('items', updated);
  };

  const addItem = () => {
    const newItem = {
      id: Date.now(),
      icon: 'Clock',
      time: '',
      title: '',
      description: '',
      location: '',
    };
    updateSchedule('items', [...items, newItem]);
  };

  const removeItem = (index) => {
    updateSchedule('items', items.filter((_, i) => i !== index));
  };

  const moveItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= items.length) return;

    const updated = [...items];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updateSchedule('items', updated);
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 shadow-romantic-sm border border-border">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="Calendar" size={20} color="var(--color-primary)" />
        </div>
        <h3 className="text-lg font-semibold text-foreground">Wedding Day Schedule</h3>
      </div>

      <div className="space-y-4">
        <Input
          label="Page Title"
          type="text"
          placeholder="Wedding Day Schedule"
          value={schedule.title || ''}
          onChange={(e) => updateSchedule('title', e.target.value)}
        />

        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-foreground uppercase tracking-wide">Schedule Items</h4>
            <Button variant="outline" size="sm" onClick={addItem} iconName="Plus" iconPosition="left">
              Add Item
            </Button>
          </div>

          <div className="space-y-3">
            {items.map((item, idx) => (
              <div key={item.id || idx} className="p-4 bg-background rounded-lg border border-border space-y-3">
                <div className="flex items-center justify-between">
                  <h5 className="text-sm font-semibold text-foreground">
                    {item.title || `Item ${idx + 1}`}
                  </h5>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveItem(idx, 'up')}
                      disabled={idx === 0}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-romantic"
                    >
                      <Icon name="ChevronUp" size={16} />
                    </button>
                    <button
                      onClick={() => moveItem(idx, 'down')}
                      disabled={idx === items.length - 1}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-romantic"
                    >
                      <Icon name="ChevronDown" size={16} />
                    </button>
                    <button
                      onClick={() => removeItem(idx)}
                      className="p-1 text-muted-foreground hover:text-error transition-romantic ml-2"
                    >
                      <Icon name="Trash2" size={16} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1.5">
                      Icon
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg shrink-0">
                        <Icon name={item.icon || 'Clock'} size={20} color="var(--color-primary)" />
                      </div>
                      <select
                        className="flex-1 h-10 px-3 bg-background border border-border rounded-lg text-sm text-foreground outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        value={item.icon || 'Clock'}
                        onChange={(e) => updateItem(idx, 'icon', e.target.value)}
                      >
                        {SCHEDULE_ICONS.map((iconOption) => (
                          <option key={iconOption.value} value={iconOption.value}>
                            {iconOption.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <Input
                    label="Time"
                    type="time"
                    placeholder="e.g., 3:00 PM"
                    value={item.time || ''}
                    onChange={(e) => updateItem(idx, 'time', e.target.value)}
                  />
                  <Input
                    label="Title"
                    type="text"
                    placeholder="e.g., Ceremony"
                    value={item.title || ''}
                    onChange={(e) => updateItem(idx, 'title', e.target.value)}
                  />
                  <Input
                    label="Venue"
                    type="text"
                    placeholder="e.g., St. Mary's Cathedral"
                    value={item.location || ''}
                    onChange={(e) => updateItem(idx, 'location', e.target.value)}
                    required
                  />
                </div>
                <Input
                  label="Address"
                  type="text"
                  placeholder="e.g., 123 Cathedral Avenue, San Francisco, Indonesia"
                  value={item.address || ''}
                  onChange={(e) => updateItem(idx, 'address', e.target.value)}
                />
                <Input
                  label="Description"
                  type="text"
                  placeholder="e.g., Join us as we exchange our vows"
                  value={item.description || ''}
                  onChange={(e) => updateItem(idx, 'description', e.target.value)}
                />
                <Input
                  label="Map URL"
                  type="text"
                  placeholder="https://maps.google.com/..."
                  value={item.mapUrl || ''}
                  onChange={(e) => updateItem(idx, 'mapUrl', e.target.value)}
                />
              </div>
            ))}

            {items.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No schedule items yet. Click "Add Item" to create your first one.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CmsScheduleSection;
